import React, { useState, useRef, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ChevronDown, type LucideIcon } from 'lucide-react';

export type NavDropdownItem =
  | { type: 'link'; to: string; icon: LucideIcon; label: string }
  | { type: 'separator' };

interface Props {
  label: string;
  items: NavDropdownItem[];
  darkMode: boolean;
  btnClass: string;
  icon?: LucideIcon;
}

const NavDropdown: React.FC<Props> = ({ label, items, darkMode, btnClass, icon: LeadingIcon }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<{ top: number; left: number; minWidth: number } | null>(
    null
  );
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const updatePosition = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const minWidth = Math.min(Math.max(rect.width, 200), window.innerWidth - 16);
    let left = rect.left;
    if (left + minWidth > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - minWidth - 8);
    }
    setMenuStyle({
      top: rect.bottom + 4,
      left,
      minWidth,
    });
  };

  useEffect(() => {
    if (!open) return;

    updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);

    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        const menu = document.getElementById(menuId);
        if (menu?.contains(e.target as Node)) return;
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, menuId]);

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  const menuPanel =
    open && menuStyle ? (
      <div
        id={menuId}
        role="menu"
        style={{
          position: 'fixed',
          top: menuStyle.top,
          left: menuStyle.left,
          minWidth: menuStyle.minWidth,
        }}
        className={`rounded-lg border shadow-xl z-[200] py-1 max-h-[70vh] overflow-y-auto max-w-[calc(100vw-1rem)] ${
          darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
        }`}
      >
        {items.map((item, i) =>
          item.type === 'separator' ? (
            <div
              key={`sep-${i}`}
              className={`my-1 border-t ${darkMode ? 'border-gray-600' : 'border-gray-100'}`}
            />
          ) : (
            <Link
              key={item.to}
              to={item.to}
              role="menuitem"
              onClick={() => setOpen(false)}
              className={`flex items-center gap-2 px-3 py-2 text-sm whitespace-nowrap ${
                darkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-700'
              }`}
            >
              <item.icon size={16} className="shrink-0 opacity-80" />
              {item.label}
            </Link>
          )
        )}
      </div>
    ) : null;

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={toggle}
        className={`px-2.5 py-2 rounded-lg border text-sm flex items-center gap-1.5 whitespace-nowrap min-h-[44px] ${btnClass}`}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {LeadingIcon && <LeadingIcon size={16} className="shrink-0" />}
        {label}
        <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {menuPanel && createPortal(menuPanel, document.body)}
    </div>
  );
};

export default NavDropdown;
